<template>
  <div class="datafast-callback">
    <div class="callback-container">
      <div
        v-if="loading"
        class="loading-state"
      >
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem"
        />
        <h3>Procesando pago...</h3>
        <p>Por favor espere mientras verificamos su transacción</p>
      </div>

      <div
        v-else-if="result"
        class="result-state"
      >
        <div
          v-if="result.success"
          class="success-result"
        >
          <div class="icon-wrapper success">
            <feather-icon
              icon="CheckCircleIcon"
              size="64"
            />
          </div>
          <h2>Pago Exitoso</h2>
          <p>{{ result.message }}</p>
          <p
            v-if="result.code"
            class="code"
          >
            Codigo: {{ result.code }}
          </p>
        </div>

        <div
          v-else
          class="error-result"
        >
          <div class="icon-wrapper error">
            <feather-icon
              icon="XCircleIcon"
              size="64"
            />
          </div>
          <h2>Pago Rechazado</h2>
          <p>{{ result.message }}</p>
        </div>

        <p class="redirect-notice">
          Redirigiendo al inicio en {{ countdown }} segundos...
        </p>
        <b-button
          variant="primary"
          @click="goToHome"
        >
          Ir al Inicio Ahora
        </b-button>
      </div>

      <div
        v-else-if="error"
        class="error-state"
      >
        <div class="icon-wrapper error">
          <feather-icon
            icon="AlertTriangleIcon"
            size="64"
          />
        </div>
        <h2>Error</h2>
        <p>{{ error }}</p>
        <b-button
          variant="primary"
          @click="goToHome"
        >
          Volver al Inicio
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'

export default {
    name: 'DataFastCallback',
    components: {
        BButton,
        BSpinner,
    },
    data() {
        return {
            loading: true,
            error: null,
            result: null,
            countdown: 5,
            countdownInterval: null,
        }
    },
    mounted() {
        this.processCallback()
    },
    beforeDestroy() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
        }
    },
    beforeRouteLeave(to, from, next) {
        next()
    },
    methods: {
        async processCallback() {
            const urlParams = new URLSearchParams(window.location.search)
            const resourcePath = urlParams.get('resourcePath')
            const id = urlParams.get('id')

            if (!resourcePath && !id) {
                this.error = 'No se encontraron datos del pago'
                this.loading = false
                this.startCountdown()
                return
            }

            const paymentData = sessionStorage.getItem('datafast_payment')
            const tokenizationData = sessionStorage.getItem(
                'datafast_tokenization'
            )

            if (tokenizationData) {
                const safeResourcePath =
                    resourcePath && typeof resourcePath === 'string'
                        ? encodeURIComponent(resourcePath)
                        : ''
                const safeId =
                    id && typeof id === 'string' ? encodeURIComponent(id) : ''

                const tokenizationUrl = `/home?resourcePath=${safeResourcePath}&id=${safeId}`
                window.location.href = tokenizationUrl
                return
            }

            if (!paymentData) {
                this.error =
                    'No se pudo recuperar la informacion del pago. Es posible que la sesion haya expirado.'
                this.loading = false
                this.startCountdown()
                return
            }

            try {
                const savedData = JSON.parse(paymentData)
                const { checkoutData } = savedData

                if (!checkoutData || !checkoutData.checkoutId) {
                    throw new Error(
                        'Datos de checkout incompletos: checkoutId faltante'
                    )
                }

                const finalResourcePath =
                    resourcePath || `/v1/checkouts/${id}/payment`

                const resp = await this.$http.post(
                    '/datafast/checkout/process',
                    {
                        paymentId: checkoutData.paymentId,
                        resourcePath: decodeURIComponent(finalResourcePath),
                        checkoutId: checkoutData.checkoutId,
                    }
                )

                if (resp?.data?.success) {
                    const d = resp.data.data || {}
                    const isSuccess = d.status === 'approved'

                    sessionStorage.removeItem('datafast_payment')

                    const userData = localStorage.getItem('userData')
                    const accessToken = localStorage.getItem('accessToken')

                    if (!userData || !accessToken) {
                        this.error =
                            'Tu sesión ha expirado. Por favor inicia sesión nuevamente.'
                        this.loading = false

                        sessionStorage.setItem(
                            'datafast_pending_result',
                            JSON.stringify({
                                success: isSuccess,
                                message: d.message || 'Transaccion procesada',
                                code: d.code || '',
                            })
                        )

                        setTimeout(() => {
                            window.location.href = '/login'
                        }, 3000)
                        return
                    }

                    sessionStorage.setItem(
                        'datafast_result',
                        JSON.stringify({
                            success: isSuccess,
                            message: d.message || 'Transaccion procesada',
                            code: d.code || '',
                        })
                    )

                    this.$router.push({ name: 'home' }).catch(() => {
                        window.location.href = '/home'
                    })

                    return
                }
                throw new Error(resp?.data?.error || 'Error al procesar pago')
            } catch (e) {
                sessionStorage.removeItem('datafast_payment')

                if (e.response?.status === 401) {
                    this.error =
                        'Tu sesión ha expirado. Por favor inicia sesión nuevamente.'
                    this.loading = false

                    setTimeout(() => {
                        window.location.href = '/login'
                    }, 3000)
                    return
                }

                const userData = localStorage.getItem('userData')
                const accessToken = localStorage.getItem('accessToken')

                if (!userData || !accessToken) {
                    this.error =
                        'Tu sesión ha expirado. Redirigiendo al login...'
                    this.loading = false

                    setTimeout(() => {
                        window.location.href = '/login'
                    }, 3000)
                    return
                }

                let errorMessage = 'Error al procesar el pago'

                try {
                    if (
                        e.response?.data?.error &&
                        typeof e.response.data.error === 'string'
                    ) {
                        errorMessage = e.response.data.error
                    } else if (e.message && typeof e.message === 'string') {
                        errorMessage = e.message
                    } else if (
                        e.response?.data?.message &&
                        typeof e.response.data.message === 'string'
                    ) {
                        errorMessage = e.response.data.message
                    }
                } catch (msgError) {
                    // Error handling
                }

                const errorCode =
                    e.response?.data?.code !== undefined &&
                    e.response?.data?.code !== null
                        ? String(e.response.data.code)
                        : ''

                this.result = {
                    success: false,
                    message: String(errorMessage || 'Error desconocido'),
                    code: errorCode,
                }

                this.loading = false
                this.startCountdown()

                
            }
        },

        startCountdown() {
            this.countdownInterval = setInterval(() => {
                this.countdown -= 1

                if (this.countdown <= 0) {
                    this.goToHome()
                }
            }, 1000)
        },

        goToHome() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval)
            }

            this.$router.push({ name: 'home' }).catch(() => {
                window.location.href = '/home'
            })
        },
    },
}
</script>

<style scoped>
.datafast-callback {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;
}

.callback-container {
    background: #fff;
    border-radius: 20px;
    padding: 40px;
    max-width: 500px;
    width: 100%;
    text-align: center;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.loading-state h3 {
    margin-top: 20px;
    color: #1e293b;
}

.loading-state p {
    color: #64748b;
    margin-top: 10px;
}

.result-state,
.error-state {
    text-align: center;
}

.icon-wrapper {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.icon-wrapper.success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: #fff;
}

.icon-wrapper.error {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: #fff;
}

.success-result h2 {
    color: #10b981;
    margin-bottom: 10px;
}

.error-result h2 {
    color: #ef4444;
    margin-bottom: 10px;
}

.result-state p,
.error-state p {
    color: #64748b;
    margin-bottom: 10px;
}

.code {
    font-family: monospace;
    background: #f1f5f9;
    padding: 8px 16px;
    border-radius: 8px;
    display: inline-block;
}

.redirect-notice {
    margin-top: 20px;
    color: #94a3b8;
    font-size: 14px;
}

.btn {
    margin-top: 15px;
    padding: 12px 30px;
    font-weight: 600;
    border-radius: 10px;
}
</style>
